import type {
  DocumentSnapshot,
  Transaction,
  UpdateData,
} from 'firebase/firestore';
import { firestoreConverter } from './firebaseConfig';

export class FirestoreService<T extends { id?: string }> {
  private collectionPath: string;
  private cache: Map<string, T> = new Map();
  private lastDoc: DocumentSnapshot | null = null;
  private unsubscribe: (() => void) | null = null;
  private paginationStack: DocumentSnapshot[] = [];
  private cacheKey: string = '';

  constructor(collectionPath: string) {
    this.collectionPath = collectionPath;
  }

  private async getFirestoreInstance() {
    const { getFirestoreInstance } = await import('./firebaseConfig');
    return await getFirestoreInstance();
  }

  async getAll(): Promise<T[]> {
    try {
      const { collection, getDocs } = await import('firebase/firestore');
      const firestore = await this.getFirestoreInstance();
      const colRef = collection(firestore, this.collectionPath);
      const querySnapshot = await getDocs(colRef);
      return querySnapshot.docs.map(
        (doc) => ({ id: doc.id, ...doc.data() } as T)
      );
    } catch (error) {
      console.error('Error getting documents:', error);
      throw new Error('Failed to get documents');
    }
  }

  // Query Collection with Caching
  async queryAll(
    searchTerm: string = '',
    sortBy: keyof T,
    pageSize: number,
    goBack: boolean = false
  ): Promise<{
    data: T[];
    lastVisible: DocumentSnapshot | null;
  }> {
    const { query, collection, where, orderBy, startAfter, limit, getDocs } =
      await import('firebase/firestore');
    const firestore = await this.getFirestoreInstance();

    this.cacheKey = `${searchTerm}-${this.paginationStack.length}`;

    if (goBack) {
      if (this.paginationStack.length > 1) {
        this.paginationStack.pop(); // ✅ Remove current page reference
        this.cacheKey = `${searchTerm}-${this.paginationStack.length - 1}`;
        this.lastDoc =
          this.paginationStack[this.paginationStack.length - 1] || null; // ✅ Get previous page reference
      } else {
        this.lastDoc = null; // ✅ If only one page left, reset to first page
        this.cacheKey = `${searchTerm}-0`;
      }
    }

    // ✅ Return cached data if available
    if (this.cache.has(this.cacheKey)) {
      console.log(`Using cached data for page ${this.cacheKey}`);
      return {
        data: (this.cache.get(this.cacheKey) as unknown as T[]) || [],
        lastVisible: this.lastDoc,
      };
    }

    // ✅ Firestore query
    let q;
    if (searchTerm) {
      q = query(
        collection(firestore, this.collectionPath),
        where('name', '>=', searchTerm),
        where('name', '<=', searchTerm + '\uf8ff'),
        orderBy(sortBy as string),
        ...(this.lastDoc ? [startAfter(this.lastDoc)] : []),
        limit(pageSize)
      );
    } else {
      q = query(
        collection(firestore, this.collectionPath),
        orderBy(sortBy as string),
        ...(this.lastDoc ? [startAfter(this.lastDoc)] : []),
        limit(pageSize)
      );
    }

    const querySnapshot = await getDocs(q);
    const lastVisible =
      querySnapshot.docs.length > 0
        ? querySnapshot.docs[querySnapshot.docs.length - 1]
        : null;
    const data = querySnapshot.docs.map(
      (doc) => ({ id: doc.id, ...doc.data() } as T)
    );

    // ✅ When moving forward, store new page reference
    if (!goBack && lastVisible) {
      this.paginationStack.push(lastVisible);
    }

    this.lastDoc = lastVisible;

    // ✅ Store results in cache
    if (this.cacheKey) {
      this.cache.set(this.cacheKey, data as unknown as T);
    }

    return { data, lastVisible };
  }

  resetPagination() {
    this.paginationStack = [];
    this.lastDoc = null;
    this.cacheKey = '';
    this.cache.clear();
  }

  async getTotalCount(): Promise<number> {
    const { collection, getCountFromServer } = await import(
      'firebase/firestore'
    );
    const firestore = await this.getFirestoreInstance();
    const colRef = collection(firestore, this.collectionPath);
    const snapshot = await getCountFromServer(colRef);
    return snapshot.data().count; // ✅ Returns total document count
  }

  async listenForUpdates(callback: (items: T[]) => void): Promise<() => void> {
    if (this.unsubscribe) this.unsubscribe();

    const { collection, onSnapshot } = await import('firebase/firestore');
    const firestore = await this.getFirestoreInstance();
    const q = collection(firestore, this.collectionPath);

    return onSnapshot(q, (snapshot) => {
      snapshot.docChanges().forEach((change) => {
        const item = { id: change.doc.id, ...change.doc.data() } as T;
        if (change.type === 'added') {
          this.cache.set(item.id!, item);
        }
        if (change.type === 'modified') {
          this.cache.set(item.id!, item);
        }
        if (change.type === 'removed') {
          this.cache.delete(item.id!);
        }
      });

      callback(Array.from(this.cache.values()));
    });
  }

  stopListening() {
    if (this.unsubscribe) {
      this.unsubscribe();
      this.unsubscribe = null;
    }
  }

  async getById(docId: string): Promise<T | null> {
    const firestore = await this.getFirestoreInstance();
    const { collection, doc, getDoc } = await import('firebase/firestore');
    const docRef = doc(collection(firestore, this.collectionPath), docId);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      const data = { id: docSnap.id, ...docSnap.data() } as T;
      return data;
    }

    return null;
  }

  // Create Document & Cache
  async create(data: Omit<T, 'id'>): Promise<string> {
    const firestore = await this.getFirestoreInstance();
    const { collection, addDoc } = await import('firebase/firestore');
    const docRef = await addDoc(
      collection(firestore, this.collectionPath),
      data
    );
    this.cache.set(docRef.id, { ...data, id: docRef.id } as T); // Cache new document
    return docRef.id;
  }

  // Update Document & Cache
  async update(docId: string, data: Partial<T>): Promise<void> {
    const firestore = await this.getFirestoreInstance();
    const { collection, doc, updateDoc } = await import('firebase/firestore');

    const docRef = doc(
      collection(firestore, this.collectionPath),
      docId
    ).withConverter(firestoreConverter<T>());

    await updateDoc(docRef, data as UpdateData<T>); // Ensure correct type
    this.cache.set(docId, { ...(this.cache.get(docId) as T), ...data });
  }

  // Delete Document & Remove from Cache
  async delete(docId: string): Promise<void> {
    try {
      const firestore = await this.getFirestoreInstance();
      const { collection, doc, deleteDoc } = await import('firebase/firestore');
      await deleteDoc(doc(collection(firestore, this.collectionPath), docId));
      this.cache.delete(docId); // Remove from cache
    } catch (error) {
      console.error('Error deleting document:', error);
    }
  }

  // Run Transaction
  async runTransaction(
    transactionFn: (transaction: Transaction) => Promise<void>
  ): Promise<void> {
    const firestore = await this.getFirestoreInstance();
    const { runTransaction } = await import('firebase/firestore');
    await runTransaction(firestore, transactionFn);
  }

  // Batch Write (Efficient for Multiple Writes)
  async batchWrite(
    operations: {
      type: 'set' | 'update' | 'delete';
      docId?: string;
      data?: T;
    }[]
  ): Promise<void> {
    const firestore = await this.getFirestoreInstance();
    const { collection, doc, writeBatch } = await import('firebase/firestore');
    const batch = writeBatch(firestore);

    operations.forEach(({ type, docId, data }) => {
      const docRef = doc(collection(firestore, this.collectionPath), docId);
      if (type === 'set' && data) batch.set(docRef, data);
      if (type === 'update' && data) batch.update(docRef, data);
      if (type === 'delete' && docId) batch.delete(docRef);
    });

    await batch.commit();
  }
}
